import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import videoCRUDServiceInstance from '../../services/VideoCRUDService';
import videoUploadServiceInstance from '../../services/VideoUploadService';
import {
    SelectLanguageComponent,
    flatLangOptions,
    getLanguagesForCreateVideo,
    groupedLangOptions,
} from '../subtitles/SelectLanguageComponent';
import { useTrackUsage } from '../../hooks/useTrackUsage';

const Text2TextDialog = ({
    open,
    onClose,
    videoId,
    onSuccess,
    folderPath,
    darkMode = false,
    themeVariant = 'vibrant',
}) => {
    useTrackUsage('Text2TextDialog', 'dialog', 'folder-browser', open);
    const [loading, setLoading] = useState(false);
    const [sourceLanguageOptions, setSourceLanguageOptions] = useState([]);
    const [selectedSource, setSelectedSource] = useState('');
    const [selectedTargets, setSelectedTargets] = useState([]);
    const [skipTranslation, setSkipTranslation] = useState(false);
    const [allLanguages, setAllLanguages] = useState([]);
    const [videoName, setVideoName] = useState('');
    const [availableLanguages, setAvailableLanguages] = useState([]);

    useEffect(() => {
        if (open && videoId) {
            setLoading(true);

            // Get all available languages
            setAllLanguages(getLanguagesForCreateVideo());

            // Fetch video details to get available languages
            videoCRUDServiceInstance
                .getVideoById(videoId)
                .then((response) => {
                    const videoData = response.data;

                    // Extract processed languages with VTT files
                    let sourceLangs = [];
                    let availableLangs = [];
                    if (videoData.videoLanguages && Array.isArray(videoData.videoLanguages)) {
                        sourceLangs = videoData.videoLanguages
                            .filter((lang) => lang.status === 'READY' && lang.vttFileUrl)
                            .map((lang) => String(lang.languageName));

                        // All languages that exist in the video (for marking purposes)
                        availableLangs = videoData.videoLanguages.map((lang) => String(lang.languageName));
                    }

                    setSourceLanguageOptions(sourceLangs);
                    setAvailableLanguages(availableLangs);
                    setVideoName(videoData.videoName);

                    // Default to English if available, otherwise first language
                    if (sourceLangs.includes('English')) {
                        setSelectedSource('English');
                    } else if (sourceLangs.length > 0) {
                        setSelectedSource(sourceLangs[0]);
                    }

                    setLoading(false);
                })
                .catch((error) => {
                    console.error('Error fetching video details:', error);
                    setLoading(false);
                    onClose();
                });
        } else {
            // Reset state when dialog closes
            setSelectedTargets([]);
            setSkipTranslation(false);
        }
    }, [open, videoId]);

    // This effect removes the selected source language from the target languages list
    useEffect(() => {
        if (selectedSource && selectedTargets.includes(selectedSource)) {
            setSelectedTargets((prev) => prev.filter((lang) => lang !== selectedSource));
        }
    }, [selectedSource, selectedTargets]);

    const handleTargetLanguagesChange = (selectedLanguageNames) => {
        // Filter out the source language and update selected targets
        const filteredTargets = selectedLanguageNames.filter((lang) => lang !== selectedSource);
        setSelectedTargets(filteredTargets);
    };

    const handleSubmit = () => {
        if (selectedTargets.length === 0) {
            return; // Don't proceed if no target languages selected
        }

        setLoading(true);

        // Get the current user's credentials
        const userData = JSON.parse(localStorage.getItem('authUser'));
        if (!userData) {
            setLoading(false);
            onClose('error', 'User authentication information not found');
            return;
        }

        // Call API to start text2text translation
        videoUploadServiceInstance
            .text2Text(
                videoId,
                videoName,
                selectedSource,
                selectedTargets.join(','),
                skipTranslation,
                userData.email,
                userData.access,
            )
            .then((response) => {
                setLoading(false);
                onSuccess({
                    sourceLanguage: selectedSource,
                    targetLanguages: selectedTargets,
                    skipTranslation: skipTranslation,
                });
                onClose(
                    'success',
                    `Translation from ${selectedSource} to ${selectedTargets.length} language(s) has been initiated.`,
                );
            })
            .catch((error) => {
                setLoading(false);
                console.error('Error starting translation:', error);
                onClose('error', `Failed to start translation: ${error.response?.data?.message || error.message}`);
            });
    };

    // Custom SelectLanguageComponent that shows status indicators
    const CustomLanguageSelector = () => {
        const [languages, setLanguages] = useState([]);

        // Initialize with current selections - FIXED
        useEffect(() => {
            if (selectedTargets.length > 0) {
                // Find the corresponding language objects from the enhanced options
                const allOptionsFlat = groupedLangOptions.reduce((acc, group) => {
                    return acc.concat(group.options || []);
                }, []);
                
                const currentSelections = allOptionsFlat.filter((lang) => selectedTargets.includes(lang.label));
                setLanguages(currentSelections);
            } else {
                setLanguages([]); // Ensure empty array when no targets selected
            }
        }, [selectedTargets, selectedSource, availableLanguages]); // Added dependencies

        const onChange = (newValue, actionMeta) => {
            console.log('onChange called:', newValue, actionMeta);
            setLanguages(newValue || []);
            const languageNames = (newValue || []).map((lang) => lang.originalLabel || lang.label);
            handleTargetLanguagesChange(languageNames);
        };

        // Create enhanced grouped options - filter out source language
        const createEnhancedGroupedOptions = () => {
            return groupedLangOptions.map((group) => ({
                ...group,
                options: group.options
                    .filter((langOption) => langOption.label !== selectedSource) // Remove source language
                    .map((langOption) => {
                        const isAlreadyAvailable = availableLanguages.includes(langOption.label);

                        return {
                            ...langOption,
                            label: isAlreadyAvailable ? `${langOption.label} (already available)` : langOption.label,
                            originalLabel: langOption.label,
                            isAlreadyAvailable: isAlreadyAvailable,
                            value: langOption.value, // Ensure value is preserved
                        };
                    }),
            }));
        };

        const enhancedGroupedOptions = createEnhancedGroupedOptions();

        const customStyles = {
            control: (base, state) => ({
                ...base,
                backgroundColor: darkMode ? '#2d2d2d' : 'white',
                borderColor: state.isFocused ? (darkMode ? '#1976d2' : '#1976d2') : darkMode ? '#444' : '#ccc',
                boxShadow: state.isFocused ? `0 0 0 1px ${darkMode ? '#1976d2' : '#1976d2'}` : 'none',
                minHeight: 38,
                '&:hover': {
                    borderColor: darkMode ? '#666' : '#999',
                },
            }),
            menu: (base) => ({
                ...base,
                backgroundColor: darkMode ? '#2d2d2d' : 'white',
                zIndex: 9999,
                border: `1px solid ${darkMode ? '#444' : '#ccc'}`,
                boxShadow: darkMode ? '0 4px 6px rgba(0, 0, 0, 0.3)' : '0 4px 6px rgba(0, 0, 0, 0.1)',
                maxHeight: '600px',
            }),
            menuPortal: (base) => ({
                ...base,
                zIndex: 9999,
            }),
            menuList: (base) => ({
                ...base,
                maxHeight: '600px',
                overflowY: 'auto',
                padding: '0',
            }),
            option: (provided, state) => ({
                ...provided,
                backgroundColor: state.isFocused
                    ? darkMode
                        ? '#333'
                        : '#f0f0f0'
                    : state.isSelected
                    ? darkMode
                        ? '#1976d2'
                        : '#1976d2'
                    : darkMode
                    ? '#2d2d2d'
                    : '#fff',
                color: state.isSelected ? '#fff' : darkMode ? '#e0e0e0' : '#000',
                cursor: 'pointer',
                padding: '10px 12px',
                fontSize: '14px',
                borderBottom: `1px solid ${darkMode ? '#333' : '#f0f0f0'}`,
            }),
            multiValue: (base) => ({
                ...base,
                backgroundColor: darkMode ? '#444' : '#e8e8e8',
            }),
            multiValueLabel: (base) => ({
                ...base,
                color: darkMode ? '#e0e0e0' : 'black',
            }),
            multiValueRemove: (base) => ({
                ...base,
                color: darkMode ? '#ccc' : '#666',
                ':hover': {
                    backgroundColor: darkMode ? '#666' : '#ccc',
                    color: 'white',
                },
            }),
            placeholder: (base) => ({
                ...base,
                color: darkMode ? '#999' : '#666',
                fontSize: '14px',
            }),
            singleValue: (base) => ({
                ...base,
                color: darkMode ? '#e0e0e0' : 'black',
            }),
            input: (base) => ({
                ...base,
                color: darkMode ? '#e0e0e0' : 'black',
            }),
            groupHeading: (provided) => ({
                ...provided,
                color: darkMode ? '#90caf9' : '#1976d2',
                fontSize: '12px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                padding: '8px 12px 4px 12px',
                margin: 0,
                backgroundColor: darkMode ? '#1e1e1e' : '#f5f5f5',
                borderBottom: `1px solid ${darkMode ? '#333' : '#e0e0e0'}`,
                position: 'sticky',
                top: 0,
                zIndex: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
            }),
        };

        return (
            <div>
                <SelectLanguageComponent
                    disabled={loading}
                    languageOptions={selectedTargets} // FIXED: Use selectedTargets to sync state
                    darkMode={darkMode}
                    themeVariant={themeVariant}
                    showQuickPresets={false}
                    customOptions={enhancedGroupedOptions}
                    customStyles={customStyles}
                    onChange={onChange}
                    placeholder="Select target languages for translation..."
                    maxMenuHeight={600}
                />
                <Typography
                    variant="caption"
                    sx={{
                        mt: 0.5,
                        color: darkMode ? '#999' : 'text.secondary',
                        display: 'block',
                    }}
                >
                    Languages marked as "already available" exist in the video but can still be re-translated.
                </Typography>
            </div>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={() => !loading && onClose()}
            fullWidth
            maxWidth="md"
            style={{ zIndex: 1300 }}
            PaperProps={{
                style: {
                    backgroundColor: darkMode ? '#2d2d2d' : 'white',
                    color: darkMode ? '#e0e0e0' : 'black',
                    overflow: 'visible',
                },
            }}
        >
            <DialogTitle sx={{ color: darkMode ? '#e0e0e0' : 'black' }}>Text-to-Text Translation</DialogTitle>

            <DialogContent dividers sx={{ backgroundColor: darkMode ? '#2d2d2d' : 'white' }}>
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress sx={{ color: darkMode ? '#90caf9' : '#1976d2' }} />
                    </Box>
                )}

                {!loading && (
                    <>
                        {sourceLanguageOptions.length === 0 ? (
                            <Typography color="error" sx={{ color: darkMode ? '#f44336' : 'error.main' }}>
                                This video does not have any processed languages that can be used as a source for
                                translation.
                            </Typography>
                        ) : (
                            <Box sx={{ p: 1 }}>
                                {/* Source Language Dropdown */}
                                <FormControl fullWidth sx={{ mb: 3 }}>
                                    <InputLabel
                                        id="source-language-label"
                                        sx={{ color: darkMode ? '#e0e0e0' : 'inherit' }}
                                    >
                                        Source Language
                                    </InputLabel>
                                    <Select
                                        labelId="source-language-label"
                                        id="source-language"
                                        value={selectedSource}
                                        label="Source Language"
                                        onChange={(e) => setSelectedSource(e.target.value)}
                                        sx={{
                                            color: darkMode ? '#e0e0e0' : 'inherit',
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: darkMode ? '#444' : 'inherit',
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: darkMode ? '#666' : 'inherit',
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: darkMode ? '#90caf9' : '#1976d2',
                                            },
                                            '& .MuiSvgIcon-root': {
                                                color: darkMode ? '#e0e0e0' : 'inherit',
                                            },
                                        }}
                                        MenuProps={{
                                            PaperProps: {
                                                style: {
                                                    backgroundColor: darkMode ? '#2d2d2d' : 'white',
                                                    color: darkMode ? '#e0e0e0' : 'black',
                                                },
                                            },
                                        }}
                                    >
                                        {sourceLanguageOptions.map((lang) => (
                                            <MenuItem
                                                key={lang}
                                                value={lang}
                                                sx={{
                                                    color: darkMode ? '#e0e0e0' : 'inherit',
                                                    '&:hover': {
                                                        backgroundColor: darkMode ? '#333' : '#f0f0f0',
                                                    },
                                                    '&.Mui-selected': {
                                                        backgroundColor: darkMode ? '#1976d2' : '#1976d2',
                                                        color: 'white',
                                                    },
                                                }}
                                            >
                                                {lang}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                {/* Target Languages using Enhanced SelectLanguageComponent */}
                                <Box sx={{ mb: 3 }}>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            mb: 2,
                                            fontWeight: 500,
                                            color: darkMode ? '#e0e0e0' : 'inherit',
                                        }}
                                    >
                                        Target Languages
                                    </Typography>
                                    <CustomLanguageSelector />
                                </Box>

                                {/* Skip Translation Checkbox */}
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={skipTranslation}
                                            onChange={(e) => setSkipTranslation(e.target.checked)}
                                            sx={{
                                                color: darkMode ? '#e0e0e0' : 'inherit',
                                                '&.Mui-checked': {
                                                    color: darkMode ? '#90caf9' : '#1976d2',
                                                },
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography sx={{ color: darkMode ? '#e0e0e0' : 'inherit' }}>
                                            Generate Empty SRT files
                                        </Typography>
                                    }
                                />
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ backgroundColor: darkMode ? '#2d2d2d' : 'white' }}>
                <Button onClick={() => onClose()} disabled={loading} sx={{ color: darkMode ? '#e0e0e0' : 'inherit' }}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                    disabled={loading || sourceLanguageOptions.length === 0 || selectedTargets.length === 0}
                    sx={{
                        backgroundColor: darkMode ? '#1976d2' : '#1976d2',
                        color: 'white',
                        '&:hover': {
                            backgroundColor: darkMode ? '#1565c0' : '#1565c0',
                        },
                        '&.Mui-disabled': {
                            backgroundColor: darkMode ? '#444' : 'rgba(0, 0, 0, 0.12)',
                            color: darkMode ? '#666' : 'rgba(0, 0, 0, 0.26)',
                        },
                    }}
                >
                    Start Translation
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default Text2TextDialog;
